import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons';
import {
  useGetInboxQuery,
  useArchiveConversationMutation
} from 'store/services/message/messageApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useIsElite } from 'hooks/useIsElite';
import { RootState } from 'store';
import { AssetUrls } from 'config/assets';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import BubbleColorPickerModal from 'components/BubbleColorPickerModal/BubbleColorPickerModal';
import './InboxPage.css';

const stripHTML = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export default function InboxPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showBubbleColorModal, setShowBubbleColorModal] = useState(false);

  const { data: meData } = useGetMeQuery();
  const { isElite } = useIsElite();
  // Redux state for rate limiting, maintenance mode, and socket connection
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);
  const isSocketConnected = useSelector((state: RootState) => state.message.isConnected);

  // Socket-based polling: Use WebSocket when connected, fallback to 60s polling when disconnected
  // When socket is connected, inbox updates in real-time via message:new events
  const pollingInterval = (isRateLimited || isMaintenanceMode)
    ? 0
    : isSocketConnected ? 0 : 60000; // No polling when connected, 60s fallback when disconnected

  const { data, isLoading, error } = useGetInboxQuery(
    { page, pageSize: 25, search: debouncedSearch || undefined },
    {
      pollingInterval,
      refetchOnMountOrArgChange: true,
    }
  );
  const [archiveConversation] = useArchiveConversationMutation();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleArchive = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await archiveConversation(conversationId).unwrap();
      toast.success('Conversation archived');
    } catch (error) {
      toast.error('Failed to archive conversation');
    }
  };

  const formatReceivedTime = (timestamp: string): string => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return messageDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    }
  };

  const handleEliteRedirect = () => {
    navigate('/elite?source=bubble-colors');
  };

  // Filter conversations (search is handled server-side, only read/unread is client-side)
  const filteredConversations = data?.conversations
    ?.filter(conv => {
      if (filter === 'unread') return conv.unreadCount > 0;
      if (filter === 'read') return conv.unreadCount === 0;
      return true;
    });

  if (isLoading) {
    return (
      <div className="inbox-page">
        <div className="inbox-header">
          <h1>Inbox</h1>
          <p className="inbox-subheader">
            Read and respond to messages sent to you by other traders in the community.
          </p>
        </div>
        <div className="inbox-loading">Loading messages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-page">
        <div className="inbox-header">
          <h1>Inbox</h1>
        </div>
        <div className="inbox-error">Failed to load messages. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="inbox-page">
      <div className="inbox-header">
        <h1>Inbox</h1>
        <p className="inbox-subheader">
          Read and respond to messages sent to you by other traders in the community.
        </p>
      </div>

      <div className="inbox-filters">
        <div className="inbox-search-container">
          <input
            type="text"
            placeholder="Search username or card name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="inbox-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="clear-search-button"
            >
              ×
            </button>
          )}
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as 'all' | 'unread' | 'read')}
          className="inbox-filter-select"
        >
          <option value="all">All</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>

        <button
          className="inbox-customize-button"
          onClick={() => setShowBubbleColorModal(true)}
          title="Customize your chat style"
          type="button"
        >
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="palette-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6F51E4" />
                <stop offset="100%" stopColor="#E02CDC" />
              </linearGradient>
            </defs>
          </svg>
          <FontAwesomeIcon icon={faPalette} />
        </button>
      </div>

      {!filteredConversations || filteredConversations.length === 0 ? (
        <div className="inbox-empty">
          {debouncedSearch ? (
            <>
              <p>No messages match your search</p>
              <button onClick={() => setSearchQuery('')} className="clear-search-button-large">
                Clear search
              </button>
            </>
          ) : (
            <>
              <img
                src={`${AssetUrls.base}/assets/illustrations/empty-inbox.svg`}
                alt="No messages"
                className="empty-inbox-illustration"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <p>No messages yet</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="inbox-table">
            <div className="inbox-table-header">
              <span className="col-username">Username</span>
              <span className="col-message">Message</span>
              <span className="col-received">Received</span>
              <span className="col-archive"></span>
            </div>

            {filteredConversations.map((conv) => (
              <div
                key={conv.conversationId}
                className={`inbox-row ${conv.unreadCount > 0 ? 'unread' : ''}`}
                onClick={() => navigate(`/inbox/${conv.conversationId}`)}
              >
                <div className="col-username">
                  <AvatarWithFlag
                    avatarUrl={conv.otherUser.avatarUrl}
                    username={conv.otherUser.username}
                    countryCode={conv.otherUser.country}
                    size={32}
                    isElite={conv.otherUser.isElite}
                    showAnimatedBorder={conv.otherUser.showAnimatedBorder}
                    borderColor1={conv.otherUser.borderColor1}
                    borderColor2={conv.otherUser.borderColor2}
                  />
                  <UsernameLink
                    username={conv.otherUser.username}
                    isElite={conv.otherUser.isElite}
                    className="inbox-username inbox-username--link"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {conv.unreadCount > 0 && (
                    <span className="inbox-unread-count">{conv.unreadCount}</span>
                  )}
                </div>
                <div className="col-message">{stripHTML(conv.lastMessage)}</div>
                <div className="col-received">{formatReceivedTime(conv.lastMessageAt)}</div>
                <div className="col-archive">
                  <button
                    className="archive-button"
                    onClick={(e) => handleArchive(conv.conversationId, e)}
                    title="Archive conversation"
                  >
                    <img
                      src={AssetUrls.icons.dropdowns.close}
                      alt="Archive"
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {data?.pagination && (
            <div className="inbox-pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.ceil(data.pagination.total / data.pagination.pageSize)}
              </span>
              <button
                disabled={!data.pagination.hasMore}
                onClick={() => setPage(p => p + 1)}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      <BubbleColorPickerModal
        isOpen={showBubbleColorModal}
        onClose={() => setShowBubbleColorModal(false)}
        currentColor1={meData?.bubbleColor1 || '#8B5CF6'}
        currentColor2={meData?.bubbleColor2 || '#6D28D9'}
        onSaveOverride={!isElite ? handleEliteRedirect : undefined}
      />
    </div>
  );
}
